import axios from 'axios';
import type {
  Employee, Department, Designation,
  LeaveType, LeaveRequest, LeaveBalance, Holiday,
  AttendanceEntry,
  EmployeeDocument,
  Expense,
  PayrollRun, PayrollTemplate, PayrollTemplateComponent, Payslip,
  WorkflowTemplate, WorkflowInstance, WorkflowStep,
  Ticket,
  InternalNotification,
  IssuedItem,
  MyDepartment,
  Shift,
  PerformanceCycle,
  PerformanceGoal,
  PerformanceGoalStatus,
  PerformanceGoalsResponse,
  PayrollVariableLine,
  OrgPayrollProfile,
  PayrollJurisdiction,
  WpsSubmission,
  EmployeeIdentifier,
  ComplianceSnapshot,
  EmployeeBankAccount,
} from '../types';

import { handoffToPlatform } from '../lib/platformAuth';

const PEOPLE_API_BASE: string = process.env.VITE_PEOPLE_API_URL || 'http://localhost:4000';

export const peopleApi = axios.create({ baseURL: `${PEOPLE_API_BASE}/api/people`, withCredentials: true });

// Employee.avatarUrl is a STABLE relative path (e.g. "/employees/emp_.../avatar"),
// never a raw signed URL — see sk-people-api's GET /employees/:id/avatar. This
// just anchors it to the same API host peopleApi already talks to, so every
// consumer (Directory, Profile, the sidebar clock card) renders the same way.
export const resolveAvatarSrc = (avatarUrl?: string): string | undefined =>
  avatarUrl ? `${PEOPLE_API_BASE}/api/people${avatarUrl}` : undefined;

export class ApiError extends Error {
  code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.code = code;
  }
}

peopleApi.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401) {
      // Cookie session expired — full CSRF handoff back to platform for re-auth.
      handoffToPlatform(window.location.href);
      return new Promise(() => {}); // never resolve; navigation is in flight
    }
    const body = err?.response?.data;
    const msg: string = body?.error?.message ?? body?.message ?? err?.message ?? 'Request failed';
    const code: string | undefined = body?.error?.code;
    return Promise.reject(new ApiError(msg, code));
  },
);

// ─── Employees ────────────────────────────────────────────────────────────────
export const fetchEmployees = () =>
  peopleApi.get<{ items: Employee[]; total: number }>('/employees').then(r => r.data.items);

/**
 * Link this login to an existing employee record (CC-001 CH-1.2).
 *
 * Sends NO body: the server matches on the VERIFIED phone number in the token,
 * and a client-supplied number would let anyone claim anyone's record. Replaces
 * the email auto-linking that GET /employees/me used to do silently.
 */
export const claimIdentityApi = () =>
  peopleApi.post<Employee>('/claim-identity').then(r => r.data);

export const fetchMyEmployee = () =>
  peopleApi.get<Employee>('/employees/me').then(r => r.data);

export const fetchEmployee = (id: string) =>
  peopleApi.get<Employee>(`/employees/${id}`).then(r => r.data);

// Every role's own view of who they work with — manager, department
// colleagues, HR contact. Safe for ess (unlike fetchEmployees, which is
// mss/hr-only): name/photo/job title/department only.
export const fetchMyDepartment = () =>
  peopleApi.get<MyDepartment>('/directory/my-department').then(r => r.data);

// HR-only — name/email/employment-type/status/join-date. Department,
// designation, location, and reporting manager are changed via the org
// chart's placement flow, not this endpoint.
export interface UpdateEmployeeInput {
  firstName?: string;
  lastName?: string;
  personalEmail?: string;
  personalPhone?: string;
  employmentType?: string;
  status?: string;
  joinDate?: string;
}
export const updateEmployeeApi = (id: string, input: UpdateEmployeeInput) => {
  const key = `emp_${id}_${Date.now()}`;
  return peopleApi.patch<Employee>(`/employees/${id}`, input, { headers: { 'Idempotency-Key': key } }).then(r => r.data);
};

const ALLOWED_AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_AVATAR_BYTES = 4 * 1024 * 1024;

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

// Self-service profile picture, stored in SK Drive — see sk-people-api's
// platform/drive.client.ts. Sent as inline base64 JSON, matching the
// backbone MCP tool contract other SK products (SK Hire) already use.
export const uploadAvatarApi = async (file: File) => {
  if (!ALLOWED_AVATAR_TYPES.includes(file.type)) {
    throw new Error('Please choose a JPEG, PNG, or WebP image');
  }
  if (file.size > MAX_AVATAR_BYTES) {
    throw new Error('Image must be under 4MB');
  }
  const contentBase64 = await fileToBase64(file);
  const key = `emp_me_avatar_${Date.now()}`;
  return peopleApi.put<Employee>(
    '/employees/me/avatar',
    { filename: file.name, contentType: file.type, contentBase64 },
    { headers: { 'Idempotency-Key': key } },
  ).then(r => r.data);
};

export const deleteAvatarApi = () =>
  peopleApi.delete<Employee>('/employees/me/avatar').then(r => r.data);

export const fetchDepartments = () =>
  peopleApi.get<Department[]>('/departments').then(r => r.data);

export const fetchDesignations = () =>
  peopleApi.get<Designation[]>('/designations').then(r => r.data);

// ─── Leave ────────────────────────────────────────────────────────────────────
export const fetchLeaveTypes = () =>
  peopleApi.get<LeaveType[]>('/leave/types').then(r => r.data);

export const fetchLeaveRequests = (employeeId?: string) =>
  peopleApi.get<LeaveRequest[]>('/leave/requests', { params: employeeId ? { employeeId } : undefined }).then(r => r.data);

export const fetchLeaveBalances = (employeeId: string) =>
  peopleApi.get<LeaveBalance[]>(`/leave/balances/${employeeId}`).then(r => r.data);

export const fetchHolidays = () =>
  peopleApi.get<Holiday[]>('/holidays').then(r => r.data);

export interface HolidayCalendar { id: string; name: string; isActive: boolean; }
export const fetchHolidayCalendars = () =>
  peopleApi.get<HolidayCalendar[]>('/holiday-calendars').then(r => r.data);

export const createHolidayCalendarApi = (name: string) =>
  peopleApi.post<HolidayCalendar>('/holiday-calendars', { name }).then(r => r.data);

export interface AddHolidayInput {
  calendarId: string;
  holidayDate: string;
  name: string;
  type: 'national' | 'regional' | 'optional';
}
export const addHolidayApi = (input: AddHolidayInput) =>
  peopleApi.post<Holiday>('/holidays', input).then(r => r.data);

export const createLeaveTypeApi = (input: { code: string; name: string }) =>
  peopleApi.post<LeaveType>('/leave/types', input).then(r => r.data);

export interface SubmitLeaveInput {
  employeeId: string;
  leaveTypeId: string;
  startDate: string;
  endDate: string;
  days: number;
  reason?: string;
}
export const submitLeaveRequest = (input: SubmitLeaveInput) => {
  const key = `lr_${input.employeeId}_${Date.now()}`;
  return peopleApi.post<LeaveRequest>('/leave/requests', input, { headers: { 'Idempotency-Key': key } }).then(r => r.data);
};

export const decideLeaveRequest = (id: string, decision: 'approved' | 'rejected', rejectionReason?: string) =>
  peopleApi.post<LeaveRequest>(`/leave/requests/${id}/decision`, { decision, rejectionReason }).then(r => r.data);

export const cancelLeaveRequest = (id: string) =>
  peopleApi.post<LeaveRequest>(`/leave/requests/${id}/cancel`).then(r => r.data);

// ─── Attendance ────────────────────────────────────────────────────────────────
export const fetchAttendance = (employeeId: string) =>
  peopleApi.get<AttendanceEntry[]>(`/attendance/${employeeId}`).then(r => r.data);

export interface TeamAttendanceEntry {
  employeeId: string;
  lastPunchType: 'clock_in' | 'clock_out';
  lastPunchAt: string;
  punchCount: number;
}
export const fetchTeamAttendance = (date: string): Promise<TeamAttendanceEntry[]> =>
  peopleApi.get<TeamAttendanceEntry[]>('/attendance/team', { params: { date } }).then(r => r.data);

// Shift.startTime/endTime are Prisma @db.Time values — serialized as a
// 1970-01-01 ISO datetime, so callers must read them with a UTC formatter
// (see fmtShiftTime in lib/attendanceWeek.ts) or local-timezone conversion
// will shift the displayed hour.
export const fetchShifts = () =>
  peopleApi.get<Shift[]>('/shifts').then(r => r.data);

export const clockPunchApi = (employeeId: string, punchType: 'clock_in' | 'clock_out') => {
  const key = `punch_${employeeId}_${Date.now()}`;
  return peopleApi.post<AttendanceEntry>('/attendance/punch', { employeeId, kind: punchType, eventAt: new Date().toISOString() }, { headers: { 'Idempotency-Key': key } }).then(r => r.data);
};

export const regularizeApi = (employeeId: string, date: string, time: string, punchType: 'clock_in' | 'clock_out', reason: string) => {
  const key = `reg_${employeeId}_${Date.now()}`;
  return peopleApi.post<AttendanceEntry>('/attendance/regularize', {
    employeeId,
    kind: punchType,
    eventAt: `${date}T${time}:00`,
    correctsDate: date,
    reasonCode: reason,
  }, { headers: { 'Idempotency-Key': key } }).then(r => r.data);
};

// ─── Payroll extensions: variable lines, profile, WPS, identifiers ───────────
// (CC-001 CH-2/3/4/5)

export const fetchVariableLines = (params: { employeeId?: string; periodCode?: string; runId?: string }) =>
  peopleApi.get<PayrollVariableLine[]>('/payroll/variable-lines', { params }).then(r => r.data);

export const fetchPayrollProfile = () =>
  peopleApi.get<OrgPayrollProfile | null>('/payroll/profile').then(r => r.data);

export interface UpsertPayrollProfileInput {
  jurisdiction: PayrollJurisdiction;
  payrollCutoffDay: number;
  molEstablishmentId?: string;
  agentBankCode?: string;
  effectiveFrom: string;
}
export const upsertPayrollProfileApi = (input: UpsertPayrollProfileInput) =>
  peopleApi.post<OrgPayrollProfile>('/payroll/profile', input).then(r => r.data);

export const fetchWpsSubmission = (runId: string) =>
  peopleApi.get<WpsSubmission>(`/payroll/runs/${runId}/wps`).then(r => r.data);

export const generateSifApi = (runId: string) =>
  peopleApi.post<WpsSubmission>(`/payroll/runs/${runId}/wps/sif`).then(r => r.data);

export const acknowledgeWpsApi = (runId: string) =>
  peopleApi.post<WpsSubmission>(`/payroll/runs/${runId}/wps/acknowledge`).then(r => r.data);

export const rejectWpsApi = (runId: string, reason: string) =>
  peopleApi.post<WpsSubmission>(`/payroll/runs/${runId}/wps/reject`, { reason }).then(r => r.data);

/** Opens in a new tab — the endpoint answers with Content-Disposition: inline. */
export const payslipPdfUrl = (payslipId: string) =>
  `${PEOPLE_API_BASE}/api/people/payroll/payslips/${payslipId}/pdf`;

export const fetchIdentifiers = (employeeId: string) =>
  peopleApi.get<EmployeeIdentifier[]>(`/employees/${employeeId}/identifiers`).then(r => r.data);

export interface UpsertIdentifierInput {
  idType: string;
  value: string;
  issuingAuthority?: string;
  issuedOn?: string;
  expiresOn?: string;
}
export const upsertIdentifierApi = (employeeId: string, input: UpsertIdentifierInput) =>
  peopleApi.post<EmployeeIdentifier>(`/employees/${employeeId}/identifiers`, input).then(r => r.data);

// "Invite to sign in" from the profile: People records which employee an
// invited address is for; the platform sends the invitation itself.
export const inviteEmployeeApi = (employeeId: string, email?: string) =>
  peopleApi.post<Employee>(`/employees/${employeeId}/invite`, email ? { email } : {}).then(r => r.data);

export const cancelEmployeeInviteApi = (employeeId: string) =>
  peopleApi.delete<Employee>(`/employees/${employeeId}/invite`).then(r => r.data);

export const fetchComplianceSnapshot = (employeeId: string) =>
  peopleApi.get<ComplianceSnapshot>(`/employees/${employeeId}/compliance-snapshot`).then(r => r.data);

export const fetchExpiringIdentifiers = (days = 60) =>
  peopleApi.get<EmployeeIdentifier[]>('/identifiers/expiring', { params: { days } }).then(r => r.data);

export const fetchBankAccounts = (employeeId: string) =>
  peopleApi.get<EmployeeBankAccount[]>(`/employees/${employeeId}/bank-accounts`).then(r => r.data);

export interface AddBankAccountInput {
  employeeId: string;
  accountHolder: string;
  accountNumber: string;
  bankCode?: string;
  isPrimary?: boolean;
}
export const addBankAccountApi = (input: AddBankAccountInput) =>
  peopleApi.post<EmployeeBankAccount>('/bank-accounts', input).then(r => r.data);

export const deactivateBankAccountApi = (id: string) =>
  peopleApi.post<EmployeeBankAccount>(`/bank-accounts/${id}/deactivate`).then(r => r.data);

// ─── Documents ────────────────────────────────────────────────────────────────
export const fetchEmployeeDocuments = (employeeId: string) =>
  peopleApi.get<EmployeeDocument[]>(`/employees/${employeeId}/documents`).then(r => r.data);

export const fetchExpiringDocs = (days = 60) =>
  peopleApi.get<EmployeeDocument[]>('/documents/expiring', { params: { days } }).then(r => r.data);

export interface CreateEmployeeDocumentInput {
  employeeId: string;
  title: string;
  storageRef?: string;
  version?: number;
  expiresOn?: string;
  retention?: 'statutory_locked' | 'business' | 'non_statutory_erasable';
}
export const createEmployeeDocumentApi = (input: CreateEmployeeDocumentInput) => {
  const key = `doc_${input.employeeId}_${Date.now()}`;
  return peopleApi.post<EmployeeDocument>('/documents', input, { headers: { 'Idempotency-Key': key } }).then(r => r.data);
};

export const acknowledgeDocumentApi = (documentId: string, employeeId: string, version: number) => {
  const key = `ack_${documentId}_${Date.now()}`;
  return peopleApi.post('/documents/acknowledgements', { documentId, employeeId, version }, { headers: { 'Idempotency-Key': key } });
};

// ─── Expenses ─────────────────────────────────────────────────────────────────
export const fetchExpenses = (employeeId: string) =>
  peopleApi.get<Expense[]>(`/employees/${employeeId}/expense-claims`).then(r => r.data);

export const fetchAllExpenses = () =>
  peopleApi.get<Expense[]>('/expense-claims').then(r => r.data);

export interface SubmitExpenseInput {
  employeeId: string;
  category: string;
  /**
   * MAJOR units still, because this is what the user typed and the API's
   * createExpenseClaimSchema converts it. Everything READ back is minor units.
   */
  amount: number;
  currency: string;
  description: string;
  receiptUrl?: string;
  expenseDate: string;
}
export const submitExpenseApi = (input: SubmitExpenseInput) => {
  const key = `exp_${input.employeeId}_${Date.now()}`;
  return peopleApi.post<Expense>('/expense-claims', input, { headers: { 'Idempotency-Key': key } }).then(r => r.data);
};

export const decideExpenseApi = (id: string, decision: 'approved' | 'rejected', rejectionReason?: string) =>
  peopleApi.post<Expense>(`/expense-claims/${id}/decision`, { decision, rejectionReason }).then(r => r.data);

// ─── Performance ──────────────────────────────────────────────────────────────
// No Idempotency-Key on these writes: unlike a payslip or a leave debit, a
// duplicate goal is visible on screen and one click from deletion, and the API
// deliberately does not guard them with requireIdempotencyKey.

export const fetchPerformanceCycles = () =>
  peopleApi.get<PerformanceCycle[]>('/performance-cycles').then(r => r.data);

/**
 * `employeeId` is a REQUEST, not an authority — the server discards it for ess
 * and rejects it for a manager's non-reportee. Omit it to get the caller's
 * default view (own goals for ess, self + direct reports for mss).
 */
export const fetchPerformanceGoals = (cycleId: string, employeeId?: string) =>
  peopleApi
    .get<PerformanceGoalsResponse>(`/performance-cycles/${cycleId}/goals`, {
      params: employeeId ? { employeeId } : undefined,
    })
    .then(r => r.data);

export interface CreateGoalInput {
  employeeId: string;
  title: string;
  description?: string;
  weight: number;
  startDate?: string;
  dueDate?: string;
}
export const createPerformanceGoalApi = (cycleId: string, input: CreateGoalInput) =>
  peopleApi.post<PerformanceGoal>(`/performance-cycles/${cycleId}/goals`, input).then(r => r.data);

export interface UpdateGoalInput {
  title?: string;
  description?: string;
  weight?: number;
  progressPercent?: number;
  status?: PerformanceGoalStatus;
  startDate?: string;
  dueDate?: string;
}
export const updatePerformanceGoalApi = (goalId: string, input: UpdateGoalInput) =>
  peopleApi.patch<PerformanceGoal>(`/performance-goals/${goalId}`, input).then(r => r.data);

export const deletePerformanceGoalApi = (goalId: string) =>
  peopleApi.delete<{ id: string; deleted: boolean }>(`/performance-goals/${goalId}`).then(r => r.data);

// ─── Payroll ──────────────────────────────────────────────────────────────────
export interface SalaryComponent { code: string; amount?: number; formula?: string; }
export interface SalaryStructure {
  id: string;
  employeeId: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  components: SalaryComponent[];
}
export interface CreateSalaryStructureInput {
  employeeId: string;
  effectiveFrom: string;
  effectiveTo?: string;
  components: SalaryComponent[];
}
export const fetchSalaryStructures = (employeeId: string) =>
  peopleApi.get<SalaryStructure[]>(`/employees/${employeeId}/salary-structures`).then(r => r.data);

export const createSalaryStructureApi = (input: CreateSalaryStructureInput) => {
  const key = `sal_${input.employeeId}_${Date.now()}`;
  return peopleApi.post<SalaryStructure>('/salary-structures', input, { headers: { 'Idempotency-Key': key } }).then(r => r.data);
};

export type { PayrollTemplateComponent };
export interface CreatePayrollTemplateInput {
  name: string;
  countryCode?: string;
  currency?: string;
  components: PayrollTemplateComponent[];
}
export const createPayrollTemplateApi = (input: CreatePayrollTemplateInput) => {
  const key = `ptmpl_${Date.now()}`;
  return peopleApi.post<PayrollTemplate>('/payroll/templates', input, { headers: { 'Idempotency-Key': key } }).then(r => r.data);
};

export const fetchMyPayslips = () =>
  peopleApi.get<Payslip[]>('/payroll/my-payslips').then(r => r.data);

export const fetchPayrollRuns = () =>
  peopleApi.get<PayrollRun[]>('/payroll/runs').then(r => r.data);

export const fetchPayrollTemplates = () =>
  peopleApi.get<PayrollTemplate[]>('/payroll/templates').then(r => r.data);

export const startPayrollRun = (data: { templateId: string; period: string }) =>
  peopleApi.post<{ run: PayrollRun; payslips: unknown[]; replayed: boolean }>(
    '/payroll/runs',
    data,
    { headers: { 'Idempotency-Key': `run_${data.period}_${Date.now()}` } },
  ).then(r => r.data);

export const finalizePayrollRunApi = (runId: string) =>
  peopleApi.post<PayrollRun>(`/payroll/runs/${runId}/finalize`).then(r => r.data);

export const markPayrollRunPaid = (runId: string) =>
  peopleApi.post<PayrollRun>(`/payroll/runs/${runId}/mark-paid`).then(r => r.data);

export const fetchBankFileCsv = (runId: string) =>
  peopleApi.get<string>(`/payroll/runs/${runId}/bank-file`, { responseType: 'text' }).then(r => r.data);

export const fetchPayslips = (runId: string) =>
  peopleApi.get<Payslip[]>(`/payroll/runs/${runId}/payslips`).then(r => r.data);

// ─── Workflows ────────────────────────────────────────────────────────────────
export const fetchWorkflowTemplates = () =>
  peopleApi.get<WorkflowTemplate[]>('/workflows/templates').then(r => r.data);

export const fetchWorkflowTemplate = (id: string) =>
  peopleApi.get<WorkflowTemplate>(`/workflows/templates/${id}`).then(r => r.data);

export const fetchWorkflowInstances = (employeeId?: string) =>
  peopleApi.get<WorkflowInstance[]>('/workflows/instances', { params: employeeId ? { employeeId } : undefined }).then(r => r.data);

// ─── Workflow mutations ────────────────────────────────────────────────────────
export const createWorkflowTemplateApi = (input: { name: string; trigger: string }) =>
  peopleApi.post<WorkflowTemplate>('/workflows/templates', input).then(r => r.data);

export interface AddStepInput {
  templateId: string;
  seq: number;
  stepType: string;
  title: string;
  ownerRole?: string;
  dueOffsetDays?: number;
  isBlocking?: boolean;
}
export const addWorkflowStepApi = (input: AddStepInput) =>
  peopleApi.post<WorkflowStep>('/workflows/steps', input).then(r => r.data);

export interface UpdateStepInput {
  seq?: number;
  stepType?: string;
  title?: string;
  ownerRole?: string | null;
  dueOffsetDays?: number | null;
  isBlocking?: boolean;
}
export const updateWorkflowStepApi = (stepId: string, input: UpdateStepInput) =>
  peopleApi.patch<WorkflowStep>(`/workflows/steps/${stepId}`, input).then(r => r.data);

export const deleteWorkflowStepApi = (stepId: string) =>
  peopleApi.delete(`/workflows/steps/${stepId}`).then(r => r.data);

export const completeStepApi = (stepInstanceId: string) =>
  peopleApi.post(`/workflows/step-instances/${stepInstanceId}/complete`).then(r => r.data);

export const startWorkflowApi = (templateId: string, employeeId: string) =>
  peopleApi.post('/workflows/instances', { templateId, employeeId }).then(r => r.data);

// ─── SK Tickets (cross-service) ───────────────────────────────────────────────
const TICKETS_API_BASE: string = process.env.VITE_TICKETS_API_URL || 'http://localhost:5000';

export const ticketsApi = axios.create({ baseURL: `${TICKETS_API_BASE}/v1`, withCredentials: true });

export interface TicketsResponse { data: Ticket[]; count: number; nextCursor: string | null }

export const fetchOpenTickets = (employeeId: string): Promise<TicketsResponse> =>
  ticketsApi.get<TicketsResponse>('/tickets', {
    params: { requesterId: employeeId, status: 'open', limit: 5 },
  }).then(r => r.data);

// ─── Employee mutations ────────────────────────────────────────────────────────
export const addEmployeeApi = (emp: Partial<Employee>) => {
  const key = `emp_create_${emp.email ?? ''}_${Date.now()}`;
  return peopleApi.post<Employee>('/employees', emp, { headers: { 'Idempotency-Key': key } }).then(r => r.data);
};

// ─── Issued items ─────────────────────────────────────────────────────────────
export const fetchIssuedItems = (employeeId: string) =>
  peopleApi.get<IssuedItem[]>(`/employees/${employeeId}/issued-items`).then(r => r.data);

// ─── Internal notifications ───────────────────────────────────────────────────
export const fetchNotifications = (): Promise<InternalNotification[]> =>
  peopleApi.get<InternalNotification[]>('/notifications').then(r => r.data);

export const markNotificationRead = (id: string): Promise<void> =>
  peopleApi.patch(`/notifications/${id}/read`).then(() => undefined);

export const markAllNotificationsRead = (): Promise<void> =>
  peopleApi.patch('/notifications/read-all').then(() => undefined);
